import { CellChange, Column, DefaultCellTypes, HeaderCell, NumberCell, Row } from '@silevis/reactgrid'
import { useQueryClient } from '@tanstack/react-query'
import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { getScenarioElectricityPrice, getSpotPriceForecast, ScenarioElectricityPriceData } from 'api/elplan/elplan.api'
import { getCurrency } from 'helpers/global.helper/global.helper'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { CopyFromCell, CopyFromCellTemplate } from 'ui/components/ReactGrid/CopyFromCell'
import { CustomReactGrid } from 'ui/components/ReactGrid/CustomReactGrid'
import Datetime from 'utils/datetime/datetime'
import { getDayTimeStepLabels, HOUR_MINUTES } from 'utils/datetime/dayTimeSteps'
import styles from './ElectricityPriceScenarioModalTable.module.less'
import { getUpdatedRowModels, priceDataToRowModels } from './electricityPriceScenario.helpers'
import { getScenarioName } from './electricityPriceScenarioName.helpers'
import type { ElectricityPriceScenario, RowModel, ScenarioElectricityPriceQueryData } from './types'

type ElectricityPriceScenarioModalTableProps = {
  scenarios: ElectricityPriceScenario[]
  chosenDate: {
    startTime: ISODateTime
    endTime: ISODateTime
  }
  electricityPriceData: ScenarioElectricityPriceQueryData | undefined
  onChange?: (rowModels: RowModel[]) => void
}

const SPOTPRICE_ID = 'spot'
const TEXT_WIDTH_PADDING = 30
const DEFAULT_COLUMN_WIDTH = 150
export function ElectricityPriceScenarioModalTable({
  scenarios,
  chosenDate,
  onChange,
  electricityPriceData,
}: ElectricityPriceScenarioModalTableProps): ReactElement {
  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Create a shared canvas instance
  const sharedCanvas = document.createElement('canvas')
  const sharedContext = sharedCanvas.getContext('2d')

  const times = useMemo(() => {
    return Datetime.getHoursBetween(chosenDate.startTime, chosenDate.endTime)
  }, [chosenDate])
  const hourLabels = getDayTimeStepLabels(times, HOUR_MINUTES)

  const getRowModels = (): RowModel[] => {
    return times.map((time) => {
      const scenarioValues = scenarios.reduce(
        (acc, scenario) => {
          acc[`scenario_${scenario.id}`] = undefined
          return acc
        },
        {} as { [key: string]: number | undefined }
      )
      return {
        time: time,
        scenarios: scenarioValues,
        forecast: undefined,
      }
    })
  }

  const [rowModels, setRowModels] = useState<RowModel[]>(getRowModels())
  const [forecastData, setForecastData] = useState<ScenarioElectricityPriceData[] | null>(null)

  const copyFromValues = useMemo(() => {
    const values = scenarios.map((scenario) => ({
      label: `${t("Yesterday's")} ${getScenarioName(scenario.id, digitalTwin?.translations)}`,
      value: scenario.id.toString(),
    }))

    values.push({
      label: `${t("Yesterday's")} ${t('Spotprice prognosis')}`,
      value: SPOTPRICE_ID,
    })

    return values
  }, [scenarios, digitalTwin?.translations, t])

  useEffect(() => {
    async function fetchForecastData(): Promise<void> {
      try {
        const result = await getSpotPriceForecast(
          activeSystem?.organization_name,
          activeSystem?.name,
          chosenDate.startTime,
          chosenDate.endTime
        )
        setForecastData(result)
      } catch (_error) {
        alertStore.error(t('Failed to load price forecast'))
      }
    }
    fetchForecastData()
  }, [chosenDate.startTime, chosenDate.endTime, t, activeSystem?.organization_name, activeSystem?.name])

  useEffect(() => {
    if (electricityPriceData && forecastData) {
      const mappedValues = priceDataToRowModels(times, electricityPriceData, forecastData, scenarios)
      setRowModels(mappedValues)
    }
  }, [electricityPriceData, forecastData, times, scenarios])

  const calculateTextWidth = useCallback(
    (text: string, font = 'bold 14px Arial'): number => {
      if (sharedContext) {
        sharedContext.font = font
        const calculatedWidth = sharedContext.measureText(text).width + TEXT_WIDTH_PADDING
        return Math.max(calculatedWidth, DEFAULT_COLUMN_WIDTH)
      }
      return DEFAULT_COLUMN_WIDTH
    },
    [sharedContext]
  )

  const headerTexts = useMemo(
    () => [
      t('Time'),
      ...scenarios.map((scenario) => getScenarioName(scenario.id, digitalTwin?.translations)),
      t('Spotprice prognosis'),
    ],
    [scenarios, digitalTwin?.translations, t]
  )
  const maxWidth = useMemo(() => {
    return Math.max(...headerTexts.map((text) => calculateTextWidth(text)))
  }, [headerTexts, calculateTextWidth])
  const getColumns = (): Column[] => {
    const scenarioColumns: Column[] = scenarios.map((scenario) => {
      return { columnId: `scenario_${scenario.id}`, width: maxWidth }
    })

    return [{ columnId: 'time', width: maxWidth }, ...scenarioColumns, { columnId: 'forecast', width: maxWidth }]
  }

  const getHeaderRow = (): Row<DefaultCellTypes> => {
    const scenarioCells: HeaderCell[] = scenarios.map((scenario) => {
      return {
        type: 'header',
        text: getScenarioName(scenario.id, digitalTwin?.translations),
        className: `${styles.ElectricityPriceScenarioModalTable_TextAlignRight} ${styles.ElectricityPriceScenarioModalTable_TextBold}  ${styles.ElectricityPriceScenarioModalTable_NoBottomBorder}`,
      }
    })
    return {
      rowId: 'header',
      cells: [
        {
          type: 'header',
          text: t('Time'),
          className: `${styles.ElectricityPriceScenarioModalTable_TextBold}  ${styles.ElectricityPriceScenarioModalTable_NoBottomBorder}`,
        },
        ...scenarioCells,
        {
          type: 'header',
          text: t('Spotprice prognosis'),
          className: `${styles.ElectricityPriceScenarioModalTable_TextAlignRight} ${styles.ElectricityPriceScenarioModalTable_TextBold} ${styles.ElectricityPriceScenarioModalTable_NoBottomBorder}`,
        },
      ],
    }
  }

  const getUnitRow = (): Row => {
    const currency = getCurrency()
    const unitText = `${currency}/MWh`
    const numberOfScenarios = scenarios.length

    const scenarioUnitCells: HeaderCell[] = Array(numberOfScenarios)
      .fill(null)
      .map(() => ({
        type: 'header',
        text: unitText,
        className: `${styles.ElectricityPriceScenarioModalTable_TextAlignRight} ${styles.ElectricityPriceScenarioModalTable_TextBold}`,
      }))

    return {
      rowId: 'unitHeader',
      cells: [
        { type: 'header', text: '' },
        ...scenarioUnitCells,
        {
          type: 'header',
          text: unitText,
          className: `${styles.ElectricityPriceScenarioModalTable_TextAlignRight} ${styles.ElectricityPriceScenarioModalTable_TextBold}`,
        },
      ],
    }
  }

  const getCopyFromRow = (): Row<DefaultCellTypes | CopyFromCell> => {
    const copyFromCells: CopyFromCell[] = scenarios.map((scenario, index) => {
      return {
        type: 'copyFrom',
        columnId: `scenario_${scenario.id}`,
        values: copyFromValues,
        selectedValue: '',
        className: `${index % 2 === 0 ? styles.GreyColumn : ''} ${
          styles.ElectricityPriceScenarioModalTable_TextAlignRight
        }`,
      }
    })
    return {
      rowId: 'copyFrom',
      cells: [
        { type: 'header', text: '', nonEditable: true },
        ...copyFromCells,
        {
          type: 'copyFrom',
          columnId: '',
          values: [],
          selectedValue: '',
          nonEditable: true,
          className: scenarios.length % 2 === 0 ? styles.GreyColumn : '',
        },
      ],
    }
  }

  const getReactGridRows = (scenarioValuesByTime: RowModel[]): Row<DefaultCellTypes | CopyFromCell>[] => {
    return [
      getHeaderRow(),
      getUnitRow(),
      getCopyFromRow(),
      ...scenarioValuesByTime.map<Row<DefaultCellTypes | CopyFromCell>>((value, idx) => {
        const scenarioCells = scenarios.map((scenario, index) => {
          const scenarioKey = `scenario_${scenario.id}`
          const scenarioValue = value.scenarios[scenarioKey] ?? NaN
          return {
            type: 'number',
            value: scenarioValue ?? NaN,
            className: `${index % 2 === 0 ? styles.GreyColumn : ''} ${
              (scenarioValue ?? 0) < 0 ? styles.NegativeValueBackground : ''
            }`,
          } as NumberCell
        })

        return {
          rowId: idx,
          cells: [
            {
              type: 'header',
              text: hourLabels[idx],
            },
            ...scenarioCells,
            {
              type: 'number',
              value: value.forecast ?? NaN,
              className: `${scenarios.length % 2 === 0 ? styles.GreyColumn : ''} ${
                (value.forecast ?? 0) < 0 ? styles.NegativeValueBackground : ''
              }`,
              nonEditable: true,
            },
          ],
        }
      }),
    ]
  }

  const applyNumberCellChanges = (prevValues: RowModel[], changes: CellChange<DefaultCellTypes>[]): void => {
    const newValues = [...prevValues]

    changes.forEach((change) => {
      const rowIndex = change.rowId as number
      const columnName = change.columnId as keyof RowModel

      if (rowIndex >= 0 && rowIndex < newValues.length && columnName in newValues[rowIndex].scenarios) {
        if (change.newCell.type === 'number') {
          newValues[rowIndex].scenarios[columnName] = change.newCell.value
        }
      }
    })

    setRowModels(newValues)
    onChange?.(newValues)
  }

  const applyCopyFromCellChanges = (prevValues: RowModel[], changes: CellChange<CopyFromCell>[]): void => {
    const yesterdayStartTime = Datetime.getYesterdayFromTime(chosenDate.startTime)
    const yesterdayEndTime = Datetime.getYesterdayFromTime(chosenDate.endTime)

    changes.forEach(async (change) => {
      const columnName = change.columnId

      if (change.rowId === 'copyFrom' && columnName in prevValues[0].scenarios) {
        const sourceScenarioId = Number(change.newCell.selectedValue)
        const scenario = scenarios.find((scenario) => scenario.id === sourceScenarioId)
        const propertyName = scenario?.uiconfig_override_alias.electricity_price_override_prop?.toString() ?? ''

        if (change.newCell.selectedValue === SPOTPRICE_ID) {
          try {
            const yesterdayForecast = await getSpotPriceForecast(
              activeSystem?.organization_name,
              activeSystem?.name,
              yesterdayStartTime,
              yesterdayEndTime
            )
            const updatedRowModels = getUpdatedRowModels(prevValues, columnName, yesterdayForecast[0])

            setRowModels(updatedRowModels)
            onChange?.(updatedRowModels)
          } catch (_error) {
            alertStore.error(t("Failed to load yesterday's spotprice forecast"))
          }
        } else {
          try {
            const yesterdayPrice = await getScenarioElectricityPrice(
              queryClient,
              sourceScenarioId,
              propertyName,
              yesterdayStartTime,
              yesterdayEndTime,
              [scenario?.setting_priority],
              activeSystem?.id,
              activeSystem?.primary_digital_twin?.name
            )

            const updatedRowModels = getUpdatedRowModels(prevValues, columnName, yesterdayPrice[0])

            setRowModels(updatedRowModels)
            onChange?.(updatedRowModels)
          } catch (_error) {
            alertStore.error(
              t("Failed to load yesterday's electricity price for {{scenarioName}}", {
                scenarioName: scenario?.scenario_name,
              })
            )
          }
        }
      }
    })
  }

  const handleChanges = (changes: CellChange<DefaultCellTypes | CopyFromCell>[]): void => {
    const prevValues = [...rowModels]

    const numberCellChanges = changes.filter((change): change is CellChange<NumberCell> => change.type === 'number')
    const copyFromCellChanges = changes.filter(
      (change): change is CellChange<CopyFromCell> => change.type === 'copyFrom'
    )

    if (numberCellChanges.length > 0) {
      applyNumberCellChanges(prevValues, numberCellChanges)
    } else if (copyFromCellChanges.length > 0) {
      applyCopyFromCellChanges(prevValues, copyFromCellChanges)
    }
  }

  const rows = getReactGridRows(rowModels)
  const columns = getColumns()

  return (
    <div>
      <CustomReactGrid
        rows={rows}
        columns={columns}
        customCellTemplates={{ copyFrom: new CopyFromCellTemplate() }}
        onCellsChanged={handleChanges}
        onPaste={handleChanges}
        stickyLeftColumns={1}
        stickyTopRows={2}
        enableFillHandle
        enableRangeSelection
      />
    </div>
  )
}
